import { ArrowDown, ArrowRight, ArrowUp, ArrowUpRight, Crown, Globe } from 'lucide-react';
import { useLocale } from '../../../lib/locale-context';
import { useTranslation } from '../../../lib/i18n';
import type { PresenceAction, PresenceStrip, StripAction, StripCell } from '../../../lib/presence-checklist';
import { Badge, Button, Typography } from '../ui';
import { PresenceActionButton, PresenceTextAction, usePresenceCopy } from './PresenceChecklist';
import styles from './SiteStrip.module.css';

// The Space header's site strip (spec "Site strip", mockup #strip): one row of cells — Site · Plan · Domain · Business
// email · Visits · On Wix — each a small label over a value and at most one action, then an outlined Manage button that
// opens the Online presence card. The Site cell also carries the site's Edit beside View site (`stripEditAction`: "Edit
// site" — chat + preview pane on a computer — or "Review site" on a phone). Exactly one accent: the checklist's next step, in the cell that owns it (or after the
// cells). Every decision is `presenceStrip` in lib/presence-checklist.ts (unit-tested, mirrored by both phones); this
// file only draws it. On a phone the cells form a 2-column grid, the accent's cell spans both, Manage goes full width.

const TONE_CLASS: Record<StripCell['tone'], string> = {
  warning: styles.toneWarning,
  success: styles.toneSuccess,
  danger: styles.toneDanger,
  neutral: '',
};

function TrendMark({ trend }: { trend: NonNullable<StripCell['trend']> }) {
  const Icon = trend.direction === 'up' ? ArrowUp : trend.direction === 'down' ? ArrowDown : ArrowRight;
  return (
    <span className={styles.trend}>
      <Icon size={12} aria-hidden />
      {trend.percent}%
    </span>
  );
}

function CellValue({ cell }: { cell: StripCell }) {
  const copy = usePresenceCopy();
  const { locale } = useLocale();
  const text = 'count' in cell.value ? new Intl.NumberFormat(locale).format(cell.value.count) : copy(cell.value);
  if (cell.badge) return <Badge variant="done" size="small" startIcon={<Crown size={14} aria-hidden />} label={text} className={styles.badge} />;
  return (
    <span className={styles.valueLine}>
      {cell.kind === 'site' && (
        <span className={styles.thumb}>
          {cell.thumbnailUrl ? <img src={cell.thumbnailUrl} alt="" className={styles.thumbImg} loading="lazy" /> : <Globe size={14} aria-hidden />}
        </span>
      )}
      <span className={styles.valueStack}>
        <Typography variant="p2" color="strong" component="span" noWrap className={`${styles.value} ${TONE_CLASS[cell.kind === 'visits' ? 'neutral' : cell.tone]}`}>
          {text}
          {cell.trend && (
            <span className={TONE_CLASS[cell.tone]}>
              {' '}
              <TrendMark trend={cell.trend} />
            </span>
          )}
        </Typography>
        {cell.sub && (
          <Typography variant="p3" color="secondary" component="span" noWrap>
            {cell.sub}
          </Typography>
        )}
      </span>
    </span>
  );
}

function CellAction({ cell, action, onAct }: { cell: StripCell; action: StripAction; onAct: (action: PresenceAction) => void }) {
  return action.primary ? (
    <PresenceActionButton action={action} onAct={onAct} color="accent" size="small" testId="siteStripPrimary" className={styles.primary} />
  ) : (
    <PresenceTextAction
      action={action}
      onAct={onAct}
      testId={`siteStripLink-${cell.kind}`}
      endIcon={cell.kind === 'site' ? <ArrowUpRight size={14} aria-hidden className="dir-flip" /> : undefined}
    />
  );
}

/** `onManage` absent (the bindings read failed, so there is no card to open) draws no Manage button. */
export function SiteStrip({ strip, onAct, onManage }: { strip: PresenceStrip; onAct: (action: PresenceAction) => void; onManage?: () => void }) {
  const { t } = useTranslation();
  return (
    <div className={styles.strip} data-testid="siteStrip">
      <div className={styles.cells}>
        {strip.cells.map((cell) => (
          // A Site cell with an Edit takes a phone row of its own, so View site ↗ and Edit sit on one line (as on Android).
          <div key={cell.kind} className={`${styles.cell}${cell.wide || cell.edit ? ` ${styles.wide}` : ''}`} data-testid={`siteStripCell-${cell.kind}`}>
            <Typography variant="p3" color="secondary" component="span" className={styles.label}>
              {t(cell.label.key)}
            </Typography>
            <CellValue cell={cell} />
            {cell.edit ? (
              // The Site cell's two links: View site ↗ and the site's Edit (`stripEditAction`: Edit site / Review site).
              <span className={styles.links}>
                {cell.action && <CellAction cell={cell} action={cell.action} onAct={onAct} />}
                <PresenceTextAction action={cell.edit} onAct={onAct} testId={`siteStripEdit-${cell.kind}`} />
              </span>
            ) : (
              cell.action && <CellAction cell={cell} action={cell.action} onAct={onAct} />
            )}
          </div>
        ))}
      </div>
      <div className={styles.end}>
        {strip.trailingPrimary && (
          <PresenceActionButton action={strip.trailingPrimary} onAct={onAct} color="accent" size="small" testId="siteStripPrimary" className={styles.endButton} />
        )}
        {onManage && (
          <Button variant="outlined" size="small" className={styles.endButton} data-testid="siteStripManage" onClick={onManage}>
            {t(strip.manage.label.key)}
          </Button>
        )}
      </div>
    </div>
  );
}
